import { useState } from 'react';
import { assessmentApi } from '../api/client';
import { Sparkles, Loader, CheckCircle, Edit3 } from 'lucide-react';
import toast from 'react-hot-toast';

interface GeneratedQuestion {
  questionText: string;
  questionType: string;
  options: Array<{ id: string; text: string }>;
  correctAnswer: string;
  explanation: string;
  bloomLevel: string;
  difficulty: string;
  marks: number;
}

interface Props {
  lessonId: string;
  courseId: string;
  onQuestionsReady: (questions: GeneratedQuestion[]) => void;
}

export function QuestionGenerator({
  lessonId, courseId, onQuestionsReady
}: Props) {
  const [generating, setGenerating] = useState(false);
  const [questions, setQuestions]   = useState<GeneratedQuestion[]>([]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [settings, setSettings]     = useState({
    questionCount: 5,
    difficulty:    'MEDIUM',
    questionType:  'MCQ',
  });

  const generateQuestions = async () => {
    setGenerating(true);
    setQuestions([]);
    try {
      const res = await assessmentApi.generateQuestions({
        lessonId,
        ...settings,
      });
      setQuestions(res.data);
      toast.success(
        `Generated ${res.data.length} questions! Review and edit below.`
      );
    } catch (err: any) {
      toast.error(
        err.response?.data?.message
        || 'AI generation failed — try again'
      );
    } finally {
      setGenerating(false);
    }
  };

  const updateQuestion = (
    idx: number,
    field: string,
    value: any
  ) => {
    setQuestions(prev => prev.map((q, i) =>
      i === idx ? { ...q, [field]: value } : q
    ));
  };

  const removeQuestion = (idx: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const bloomColors: Record<string, string> = {
    REMEMBER:   'bg-blue-400/20 text-blue-300',
    UNDERSTAND: 'bg-green-400/20 text-green-300',
    APPLY:      'bg-yellow-400/20 text-yellow-300',
    ANALYZE:    'bg-purple-400/20 text-purple-300',
    EVALUATE:   'bg-red-400/20 text-red-300',
    CREATE:     'bg-pink-400/20 text-pink-300',
  };

  const inputClass = `w-full bg-gray-800 border border-gray-700
    rounded-lg px-3 py-2 text-white text-sm
    focus:outline-none focus:border-green-400 resize-none`;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-green-400/20
                        flex items-center justify-center">
          <Sparkles size={16} className="text-green-400" />
        </div>
        <div>
          <h3 className="font-bold text-white text-sm">
            AI Question Generator
          </h3>
          <p className="text-xs text-gray-500">
            Generate quiz questions from lesson content automatically
          </p>
        </div>
      </div>

      {/* Settings */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">
            Number of questions
          </label>
          <select
            value={settings.questionCount}
            onChange={e => setSettings(p => ({
              ...p, questionCount: Number(e.target.value)
            }))}
            className={inputClass}
          >
            {[3,5,8,10,15].map(n => (
              <option key={n} value={n}>{n} questions</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">
            Difficulty
          </label>
          <select
            value={settings.difficulty}
            onChange={e => setSettings(p => ({
              ...p, difficulty: e.target.value
            }))}
            className={inputClass}
          >
            {['EASY','MEDIUM','HARD','MIXED'].map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">
            Question type
          </label>
          <select
            value={settings.questionType}
            onChange={e => setSettings(p => ({
              ...p, questionType: e.target.value
            }))}
            className={inputClass}
          >
            {['MCQ','TRUE_FALSE','MIXED'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Generate button */}
      <button
        onClick={generateQuestions}
        disabled={generating}
        className="w-full bg-green-400 text-gray-950 font-bold
                   py-3 rounded-xl hover:bg-green-300 transition-colors
                   disabled:opacity-50 flex items-center
                   justify-center gap-2 text-sm mb-6"
      >
        {generating ? (
          <>
            <Loader size={16} className="animate-spin" />
            Generating questions with AI...
          </>
        ) : (
          <>
            <Sparkles size={16} />
            Generate {settings.questionCount} Questions with AI
          </>
        )}
      </button>

      {/* Generated questions */}
      {questions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-white">
              {questions.length} questions generated — review before saving
            </div>
            <button
              onClick={() => onQuestionsReady(questions)}
              className="bg-blue-500 text-white font-bold
                         px-4 py-2 rounded-lg text-xs
                         hover:bg-blue-400 transition-colors
                         flex items-center gap-2"
            >
              <CheckCircle size={12} />
              Use these questions
            </button>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
            {questions.map((q, qi) => (
              <div key={qi}
                   className="border border-gray-700 rounded-xl p-4">
                <div className="flex items-start justify-between
                                gap-2 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-gray-500">
                      Q{qi + 1}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full
                      font-semibold
                      ${bloomColors[q.bloomLevel] || 'bg-gray-700 text-gray-300'}`}>
                      {q.bloomLevel}
                    </span>
                    <span className="text-xs bg-gray-800 text-gray-400
                                     px-2 py-0.5 rounded-full">
                      {q.difficulty}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingIdx(
                        editingIdx === qi ? null : qi
                      )}
                      className="text-gray-500 hover:text-green-400
                                 transition-colors"
                    >
                      <Edit3 size={12} />
                    </button>
                    <button
                      onClick={() => removeQuestion(qi)}
                      className="text-gray-500 hover:text-red-400
                                 transition-colors text-xs"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {editingIdx === qi ? (
                  <div className="space-y-2">
                    <textarea
                      value={q.questionText}
                      onChange={e => updateQuestion(
                        qi, 'questionText', e.target.value
                      )}
                      rows={2}
                      className={inputClass}
                    />
                    {q.options.map((opt, oi) => (
                      <div key={opt.id}
                           className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-4">
                          {opt.id}.
                        </span>
                        <input
                          value={opt.text}
                          onChange={e => {
                            const opts = [...q.options];
                            opts[oi] = {
                              ...opts[oi], text: e.target.value
                            };
                            updateQuestion(qi, 'options', opts);
                          }}
                          className={inputClass}
                        />
                        {q.correctAnswer === opt.id && (
                          <CheckCircle
                            size={14}
                            className="text-green-400 flex-shrink-0"
                          />
                        )}
                      </div>
                    ))}
                    <input
                      value={q.explanation}
                      onChange={e => updateQuestion(
                        qi, 'explanation', e.target.value
                      )}
                      placeholder="Explanation..."
                      className={inputClass}
                    />
                  </div>
                ) : (
                  <div>
                    <p className="text-white text-sm mb-2 leading-relaxed">
                      {q.questionText}
                    </p>
                    <div className="grid grid-cols-2 gap-1 mb-2">
                      {q.options.map(opt => (
                        <div
                          key={opt.id}
                          className={`text-xs px-2 py-1.5 rounded-lg
                            ${q.correctAnswer === opt.id
                              ? 'bg-green-400/20 text-green-300 font-semibold'
                              : 'bg-gray-800 text-gray-400'
                            }`}
                        >
                          <span className="font-bold mr-1">{opt.id}.</span>
                          {opt.text}
                        </div>
                      ))}
                    </div>
                    {q.explanation && (
                      <p className="text-xs text-gray-500 italic">
                        💡 {q.explanation}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}